import type {
  SystemEvidenceRecord,
  SystemScenarioHandlerOutput,
  SystemScenarioRunRecord,
  SystemWorkspaceDefinition,
} from './SystemWorkspaceContracts';

import {
  assertControlledScenario,
} from './SystemWorkspaceContracts';

import {
  getSystemScenarioRegistration,
} from './SystemScenarioRegistry';

export type ControlledSimulatorOptions = {
  timeoutMs?: number;
  now?: () => string;
  createRunId?: () => string;
};

export type SystemWorkspaceResolver = (
  systemId: string,
) => SystemWorkspaceDefinition | null;

const DEFAULT_TIMEOUT_MS = 10_000;

function defaultRunId(): string {
  return `sim-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function normalizeError(
  value: unknown,
): Error {
  if (value instanceof Error) {
    return value;
  }

  return new Error(String(value));
}

export class ControlledSystemSimulatorEngine {
  private readonly evidence:
    SystemEvidenceRecord[] = [];

  private readonly runs:
    SystemScenarioRunRecord[] = [];

  private readonly timeoutMs: number;

  private readonly now: () => string;

  private readonly createRunId: () => string;

  constructor(
    private readonly resolveWorkspace:
      SystemWorkspaceResolver,
    options: ControlledSimulatorOptions = {},
  ) {
    this.timeoutMs =
      options.timeoutMs ??
      DEFAULT_TIMEOUT_MS;

    this.now =
      options.now ??
      (() => new Date().toISOString());

    this.createRunId =
      options.createRunId ??
      defaultRunId;
  }

  listEvidence(
    systemId?: string,
  ): SystemEvidenceRecord[] {
    const records = systemId
      ? this.evidence.filter(
          record =>
            record.systemId === systemId,
        )
      : this.evidence;

    return [...records];
  }

  listRuns(
    systemId?: string,
  ): SystemScenarioRunRecord[] {
    const records = systemId
      ? this.runs.filter(
          run =>
            run.systemId === systemId,
        )
      : this.runs;

    return [...records];
  }

  async execute(
    systemId: string,
    scenarioId: string,
  ): Promise<SystemScenarioRunRecord> {
    const runId = this.createRunId();
    const startedAt = this.now();

    const registration =
      getSystemScenarioRegistration(
        systemId,
        scenarioId,
      );

    const rejectedRun =
      (
        reason: string,
      ): SystemScenarioRunRecord => ({
        runId,
        scenarioId,
        systemId,
        mode: 'SIMULATED',
        state: 'rejected',
        startedAt,
        completedAt: this.now(),
        passed: false,
        timedOut: false,
        rejected: true,
        error: reason,
        expected: [],
        actual: {},
        evidenceIds: [],
        cleanupRequired: false,
        cleanupAttempted: false,
        cleanupCompleted: true,
        productionMutationOccurred: false,
      });

    if (!registration) {
      const run = rejectedRun(
        'Scenario is not registered.',
      );

      this.runs.unshift(run);
      return run;
    }

    const {
      scenario,
      handler,
    } = registration;

    const workspace =
      this.resolveWorkspace(systemId);

    if (!workspace) {
      const run = rejectedRun(
        'Owning system workspace is not registered.',
      );

      this.runs.unshift(run);
      return run;
    }

    try {
      assertControlledScenario(
        scenario,
        workspace,
      );
    } catch (error) {
      const run = rejectedRun(
        normalizeError(error).message,
      );

      this.runs.unshift(run);
      return run;
    }

    if (!workspace.simulatorPolicy.enabled) {
      const run = rejectedRun(
        'Simulator is disabled for this system.',
      );

      this.runs.unshift(run);
      return run;
    }

    if (
      scenario.mode === 'LIVE' &&
      workspace.simulatorPolicy
        .requiresExplicitLiveApproval
    ) {
      const run = rejectedRun(
        'LIVE execution requires a separate explicit approval path.',
      );

      this.runs.unshift(run);
      return run;
    }

    const abortController =
      new AbortController();

    const emittedEvidence:
      SystemEvidenceRecord[] = [];

    const emitEvidence = (
      record: SystemEvidenceRecord,
    ): void => {
      if (
        record.systemId !==
        scenario.systemId
      ) {
        throw new Error(
          'Evidence system authority does not match the scenario.',
        );
      }

      if (
        record.mode !== scenario.mode
      ) {
        throw new Error(
          'Evidence mode does not match the scenario mode.',
        );
      }

      const namespacedRecord = {
        ...record,
        id:
          `${workspace.simulatorPolicy.evidenceNamespace}:` +
          `${runId}:${record.id}`,
        correlationId:
          record.correlationId ?? runId,
      };

      emittedEvidence.push(
        namespacedRecord,
      );
    };

    let output:
      SystemScenarioHandlerOutput | null =
        null;

    let executionError: Error | null =
      null;

    let timedOut = false;
    let cleanupAttempted = false;
    let cleanupCompleted =
      !workspace.simulatorPolicy
        .resetRequired;

    const timeoutError =
      new Error(
        `Scenario exceeded ${this.timeoutMs}ms timeout.`,
      );

    let timeoutHandle:
      ReturnType<typeof setTimeout> | null =
        null;

    try {
      output = await Promise.race([
        handler.execute({
          runId,
          scenario,
          workspace,
          startedAt,
          signal:
            abortController.signal,
          emitEvidence,
          now: this.now,
        }),
        new Promise<never>(
          (_, reject) => {
            timeoutHandle = setTimeout(
              () => {
                timedOut = true;
                abortController.abort();
                reject(timeoutError);
              },
              this.timeoutMs,
            );
          },
        ),
      ]);

      for (
        const record of
        output.evidence
      ) {
        emitEvidence(record);
      }

      if (
        output.productionMutationOccurred &&
        !workspace.simulatorPolicy
          .productionWritesAllowed
      ) {
        throw new Error(
          'Scenario reported a prohibited production mutation.',
        );
      }
    } catch (error) {
      executionError =
        normalizeError(error);
    } finally {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }

      if (
        workspace.simulatorPolicy
          .resetRequired
      ) {
        cleanupAttempted = true;

        if (!handler.cleanup) {
          cleanupCompleted = false;

          if (!executionError) {
            executionError =
              new Error(
                'Required scenario cleanup handler is missing.',
              );
          }
        } else {
          try {
            await handler.cleanup({
              runId,
              scenario,
              workspace,
              output,
              error: executionError,
              signal:
                abortController.signal,
            });

            cleanupCompleted = true;
          } catch (cleanupError) {
            cleanupCompleted = false;

            const message =
              normalizeError(
                cleanupError,
              ).message;

            executionError =
              new Error(
                executionError
                  ? `${executionError.message}; cleanup failed: ${message}`
                  : `Cleanup failed: ${message}`,
              );
          }
        }
      }
    }

    const productionMutationOccurred =
      output
        ?.productionMutationOccurred ??
      false;

    const passed =
      Boolean(output?.passed) &&
      !executionError &&
      !timedOut &&
      cleanupCompleted &&
      !productionMutationOccurred;

    const run: SystemScenarioRunRecord = {
      runId,
      scenarioId,
      systemId,
      mode: scenario.mode,
      state: timedOut
        ? 'timed_out'
        : passed
          ? 'passed'
          : 'failed',
      startedAt,
      completedAt: this.now(),
      passed,
      timedOut,
      rejected: false,
      error:
        executionError?.message ??
        null,
      expected:
        scenario.expectations,
      actual:
        output?.actual ?? {},
      evidenceIds:
        emittedEvidence.map(
          record => record.id,
        ),
      cleanupRequired:
        workspace.simulatorPolicy
          .resetRequired,
      cleanupAttempted,
      cleanupCompleted,
      productionMutationOccurred,
    };

    this.evidence.unshift(
      ...emittedEvidence,
    );

    this.runs.unshift(run);

    return run;
  }
}
